import React, { useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import momentTimezone from 'moment-timezone';

import useTip from 'hooks/useTip.hook';
import {
  selectCurrentSalonAddress1,
  selectCurrentSalonCity,
  selectCurrentSalonCountryCode,
  selectCurrentSalonCurrency,
  selectCurrentSalonCurrencySign,
  selectCurrentSalonFlags,
  selectCurrentSalonId,
  selectCurrentSalonLogo,
  selectCurrentSalonName,
  selectCurrentSalonPostcode,
  selectCurrentSalonRestrictedToCriticalServices,
  selectCurrentSalonTimeZone,
} from 'selectors/currentSalon.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import olen from 'utils/objects/olen.util';
import fromComponentToName from 'utils/react/fromComponentToName.util';

const L = logger('SalonBasicInfoProvider');

const Context = React.createContext();

const normalizeAmericaTimezone = (timezone) => {
  if (!timezone) {
    return;
  }

  // Add more checks in the future, extract into a function if necessary.
  return timezone.replace('US/', 'America/');
};

const Provider = (props) => {
  const { salonId, salonName, salonLogo, salonCity, address1, postcode, children } = props;
  const { isRestrictedToCriticalServices, salonFlags } = props;
  const { currency, currencySign, timezone, countryCode } = props;

  useEffect(() => {
    if (!timezone) {
      return;
    }

    momentTimezone.tz.setDefault(normalizeAmericaTimezone(timezone));
  }, [timezone]);

  const value = useMemo(() => {
    const basic = { salonId, salonName, salonLogo, salonCity, address1, postcode };
    const flags = { isRestrictedToCriticalServices, salonFlags };
    const gilt = { currency, currencySign, countryCode };

    const result = { ...basic, ...flags, ...gilt };
    L.info('()', ...L.O(result));

    return result;
  }, [countryCode, salonId, salonName, salonLogo, salonCity, address1, postcode, isRestrictedToCriticalServices, salonFlags, currency, currencySign]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const SalonBasicInfoProvider = connect((state) => ({
  // basic info
  salonId: selectCurrentSalonId(state),
  salonName: selectCurrentSalonName(state),
  address1: selectCurrentSalonAddress1(state),
  salonCity: selectCurrentSalonCity(state),
  postcode: selectCurrentSalonPostcode(state),
  salonLogo: selectCurrentSalonLogo(state),
  // restriction status
  isRestrictedToCriticalServices: selectCurrentSalonRestrictedToCriticalServices(state),
  // flags sent from BE
  salonFlags: selectCurrentSalonFlags(state),
  // GILT
  countryCode: selectCurrentSalonCountryCode(state),
  currency: selectCurrentSalonCurrency(state),
  currencySign: selectCurrentSalonCurrencySign(state),
  timezone: selectCurrentSalonTimeZone(state),
}))(Provider);

export const useSalonBasicInfo = () => {
  const { salonId, salonName, address1, salonCity, postcode, salonLogo } = useContext(Context);
  return useMemo(() => ({ salonId, salonName, address1, salonCity, postcode, salonLogo }), [salonId, salonName, address1, salonCity, postcode, salonLogo]);
};

export const useCurrentSalonId = () => {
  const value = useContext(Context);
  return value?.salonId;
};

export const useSalonFlags = () => {
  const value = useContext(Context);
  const flags = value?.salonFlags;

  return useMemo(() => (olen(flags) ? Object.freeze({ ...flags }) : asObject()), [flags]);
};

export const useSingleSalonFlag = (flag) => {
  const flags = useSalonFlags();

  return useMemo(() => {
    const flagName = fromAnyToTrimmed(flag);
    if (!flagName) L.error('Invalid flag:', flag);

    return flags?.[flagName];
  }, [flag, flags]);
};

export const useIsSalonRestrictedToCriticalServices = () => {
  const value = useContext(Context);
  return fromAnyToBool(value?.isRestrictedToCriticalServices);
};

export const withSalonRestriction = (Component) => {
  // find out the name of the wrapped component or use a default one
  const displayName = `withSalonRestriction(${fromComponentToName(Component)})`;

  // create the wrapper component and use it to render the wrapped one and also
  // remove the props added by connect from the props for the child component
  const WithSalonRestriction = (props) => {
    const isRestrictedToCriticalServices = useIsSalonRestrictedToCriticalServices();
    const { val: isRestrictionCheckEnabled, yay: enableRestrictionCheck, nay: disableRestrictionCheck } = useTip(false);

    return (
      <Component
        {...props}
        isRestrictedToCriticalServices={isRestrictedToCriticalServices}
        isRestrictionCheckEnabled={isRestrictionCheckEnabled}
        enableRestrictionCheck={enableRestrictionCheck}
        disableRestrictionCheck={disableRestrictionCheck}
      />
    );
  };

  // static fields must be copied from the wrapped to the wrapper
  hoistNonReactStatics(WithSalonRestriction, Component);

  // nice display name for the wrapper
  WithSalonRestriction.displayName = displayName;

  return WithSalonRestriction;
};
